import React, { useEffect, useState } from 'react';
import axios from '../api';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Typography,
  Box,
  Autocomplete,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useToast } from './ToastProvider';
import {
  labelSx,
  inputFieldSx,
  modalPaperSx,
  dialogTitleSx,
} from '../styles/tokens';

const sectionTitleSx = {
  fontSize: '0.9375rem',
  fontWeight: 500,
  color: 'text.primary',
  mb: 1.5,
  display: 'block',
  lineHeight: 1.45,
};

const autocompleteSx = {
  width: '100%',
  minWidth: 0,
  '& .MuiAutocomplete-inputRoot': {
    width: '100%',
  },
};

const renderSearchInput = (placeholder) => (params) => (
  <TextField
    {...params}
    placeholder={placeholder}
    required
    size="small"
    fullWidth
    sx={{ ...inputFieldSx, width: '100%' }}
  />
);

const renderSearchOption = (primary, secondary) => (props, option) => {
  const { key, ...optionProps } = props;
  return (
    <li key={key} {...optionProps}>
      <Box>
        <Typography sx={{ fontSize: '0.875rem' }}>{primary(option)}</Typography>
        {secondary(option) && (
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{secondary(option)}</Typography>
        )}
      </Box>
    </li>
  );
};

const filterProducts = (options, { inputValue }) => {
  const search = inputValue.toLowerCase().trim();
  if (!search) return options;
  return options.filter((opt) => {
    const name = (opt.name || '').toLowerCase();
    const code = (opt.product_code || '').toLowerCase();
    const category = (opt.category || '').toLowerCase();
    return name.includes(search) || code.includes(search) || category.includes(search);
  });
};

const getCustomerLabel = (customer) => customer.company_name || customer.name || '';

const getProductLabel = (product) => product.name || '';

const filterCustomers = (options, { inputValue }) => {
  const search = inputValue.toLowerCase().trim();
  if (!search) return options;
  return options.filter((c) => {
    const name = (c.name || '').toLowerCase();
    const company = (c.company_name || '').toLowerCase();
    const email = (c.email || '').toLowerCase();
    const place = (c.place || '').toLowerCase();
    const phone = (c.ph1 || '').toLowerCase();
    return (
      name.includes(search) ||
      company.includes(search) ||
      email.includes(search) ||
      place.includes(search) ||
      phone.includes(search)
    );
  });
};

const QuotationForm = ({ closeModal }) => {
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [formData, setFormData] = useState({
    customer_id: '',
    event_date: '',
    return_date: '',
    products: [{ product_id: '', quantity: '' }],
  });
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const showToast = useToast();

  useEffect(() => {
    fetchProducts();
    axios.get('/api/customer').then(res => setCustomers(res.data));
  }, []);

  const fetchProducts = async () => {
    const res = await axios.get('/api/products', { params: { status: 'active' } });
    setProducts(res.data);
  };

  const handleGeneralChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleProductChange = (index, field, value) => {
    const newProducts = [...formData.products];
    newProducts[index][field] = value;
    setFormData(prev => ({ ...prev, products: newProducts }));
  };

  const addProductRow = () => {
    setFormData(prev => ({
      ...prev,
      products: [...prev.products, { product_id: '', quantity: '' }]
    }));
  };

  const removeProductRow = index => {
    if (formData.products.length === 1) return;
    const newProducts = formData.products.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, products: newProducts }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg('');
    try {
      const payload = {
        customer_id: parseInt(formData.customer_id),
        event_date: formData.event_date || null,
        return_date: formData.return_date || null,
        products: formData.products.map(p => ({
          product_id: parseInt(p.product_id),
          quantity: parseInt(p.quantity)
        }))
      };
      await axios.post('/api/quotations', payload);
      showToast('Quotation created successfully', 'success');
      closeModal();
    } catch (err) {
      let msg = 'Failed to create quotation.';
      if (err.response?.data?.error) msg = err.response.data.error;
      setErrorMsg(msg);
      showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open
      onClose={closeModal}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: modalPaperSx }}
      sx={{ '& .MuiDialog-paper': { mx: { xs: 1, sm: 2 }, width: { xs: 'calc(100% - 16px)', sm: '100%' } } }}
    >
      <DialogTitle sx={dialogTitleSx}>
        <span>New Quotation</span>
        <IconButton size="small" onClick={closeModal} sx={{ color: 'text.secondary' }} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ overflow: 'auto', flex: 1, px: { xs: 2, sm: 3 }, py: 2 }}>
        {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}
        <Box component="form" onSubmit={handleSubmit} id="quotation-form">
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2.5, width: '100%' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={labelSx}>Customer</Typography>
              <Autocomplete
                options={customers}
                value={customers.find((c) => String(c.id) === String(formData.customer_id)) || null}
                onChange={(_, newValue) =>
                  setFormData((prev) => ({ ...prev, customer_id: newValue ? String(newValue.id) : '' }))
                }
                getOptionLabel={getCustomerLabel}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={filterCustomers}
                renderOption={renderSearchOption(
                  (option) => option.company_name || option.name,
                  (option) =>
                    option.company_name && option.name
                      ? option.name
                      : option.email || option.ph1 || option.place || ''
                )}
                renderInput={renderSearchInput('Search customer...')}
                fullWidth
                size="small"
                sx={autocompleteSx}
              />
            </Box>
            {/* Spacer matches Qty + remove so Customer and Product stay equal width */}
            <Box sx={{ width: { xs: 140, sm: 152 }, flexShrink: 0 }} aria-hidden />
          </Box>

          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            <Grid item xs={12} sm={6}>
              <Typography sx={labelSx}>Event Date</Typography>
              <TextField
                type="date"
                name="event_date"
                value={formData.event_date}
                onChange={handleGeneralChange}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={inputFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={labelSx}>Return Date</Typography>
              <TextField
                type="date"
                name="return_date"
                value={formData.return_date}
                onChange={handleGeneralChange}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={inputFieldSx}
              />
            </Grid>
          </Grid>

          <Typography sx={sectionTitleSx}>Products</Typography>
          {formData.products.map((p, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                mb: 1.5,
                width: '100%',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={labelSx}>Product</Typography>
                <Autocomplete
                  options={products}
                  value={products.find((pr) => String(pr.id) === String(p.product_id)) || null}
                  onChange={(_, newValue) =>
                    handleProductChange(index, 'product_id', newValue ? String(newValue.id) : '')
                  }
                  getOptionLabel={getProductLabel}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  filterOptions={filterProducts}
                  renderOption={renderSearchOption(
                    (option) => option.name,
                    (option) => {
                      const details = [
                        option.product_code,
                        option.category,
                        `₹${option.price ?? 0}`,
                      ].filter(Boolean);
                      return details.join(' · ');
                    }
                  )}
                  renderInput={renderSearchInput('Search product...')}
                  fullWidth
                  size="small"
                  sx={autocompleteSx}
                />
              </Box>
              <Box sx={{ width: { xs: 88, sm: 100 }, flexShrink: 0 }}>
                <Typography sx={labelSx}>Qty *</Typography>
                <TextField
                  type="number"
                  min="1"
                  value={p.quantity}
                  onChange={e => handleProductChange(index, 'quantity', e.target.value)}
                  required
                  fullWidth
                  size="small"
                  placeholder="Qty"
                  sx={inputFieldSx}
                />
              </Box>
              <Box sx={{ flexShrink: 0, pt: 2.5 }}>
                <IconButton
                  size="small"
                  onClick={() => removeProductRow(index)}
                  disabled={formData.products.length === 1}
                  sx={{
                    color: 'text.secondary',
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': { bgcolor: 'action.hover' },
                    '&.Mui-disabled': { opacity: 0.5 },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Box>
          ))}
          <Button
            type="button"
            variant="outlined"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            onClick={addProductRow}
            sx={{ mb: 1, textTransform: 'none', fontSize: '0.8125rem' }}
          >
            Add Product
          </Button>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
        <Button variant="outlined" onClick={closeModal} disabled={loading} sx={{ textTransform: 'none', fontWeight: 500 }}>Cancel</Button>
        <Button type="submit" form="quotation-form" variant="contained" color="primary" disabled={loading} sx={{ textTransform: 'none', fontWeight: 500 }}>
          {loading ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CircularProgress size={18} sx={{ color: 'white' }} />Creating...</Box> : 'Create Quotation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuotationForm;
